import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Clock, CreditCard, Download, LogOut, MessageCircle, Mic, Receipt, Reply, Search, Send, Share2, Square, Wifi, X } from "lucide-react";
import { toast } from "sonner";
import { playPortalReply } from "../lib/notifySound";
import { api, errMsg, fmtDate, fmtDateTime, rupiah, PORTAL_TOKEN_KEY } from "../lib/api";
import { PortalTransferCard } from "../components/PortalTransferCard";
import { PortalHistory } from "../components/PortalHistory";

const API_BASE = api.defaults.baseURL;
import { bulanTagihan, downloadInvoice, fetchInvoiceNo, jatuhTempo10, shareInvoiceWA } from "../lib/invoiceImage";

const buildPortalInvoice = async (info, amount, order) => {
  const lunas = order && order.status === "lunas";
  return {
    no: await fetchInvoiceNo(info.name, info.expired_date),
    nama: info.name, alamat: info.cabang || "-",
    paket: info.profile || "WiFi Rumahan", bulan: bulanTagihan(order?.expired_date || info.expired_date),
    harga: amount, ppn: 0, diskon: order ? order.unique_code : 0, total: order ? order.amount_pay : amount,
    tanggal: new Date().toISOString(), jatuh_tempo: jatuhTempo10(order?.expired_date || info.expired_date),
    catatan: lunas ? `LUNAS — dibayar ${rupiah(order.amount_pay)} via transfer ${order.bank?.bank || ""} ${order.bank?.account_fmt || ""} a.n. ${order.bank?.holder || ""}, dikonfirmasi admin. Internet aktif s/d ${fmtDate(order.expired_date)}. Simpan invoice ini sebagai bukti pembayaran.` : undefined,
  };
};

const STATUS_MAP = {
  online: { label: "Aktif — Online", cls: "text-emerald-400" },
  active: { label: "Aktif — Offline", cls: "text-rose-400" },
  expired: { label: "Belum Perpanjang", cls: "text-slate-400" },
  disabled: { label: "Nonaktif", cls: "text-slate-400" },
};

const parsePriceFromProfile = (profile) => {
  const m = /bayar\s*([\d.]+)/i.exec(profile || "");
  if (!m) return 0;
  return parseInt(m[1].replace(/\./g, ""), 10) || 0;
};

const shortProfile = (p) => { const m = /(\d+)\s*MBPS/i.exec(p || ""); return m ? `PAKET-${m[1]}MBPS` : (p || "-"); };

const daysLeft = (d) => { if (!/^\d{4}-\d{2}-\d{2}/.test(d || "")) return null; return Math.ceil((new Date(d.slice(0, 10) + "T00:00:00") - new Date(new Date().toDateString())) / 86400000); };

const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtTgl = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || ""); return m ? `${Number(m[3])} ${BULAN[Number(m[2]) - 1]} ${m[1]}` : (d || "Belum diatur"); };

const CARD = "rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm";
const MUTED = "text-slate-400";

const LS_SECRET = "fasnet_portal_secret";
const LS_READ = "fasnet_portal_chat_read";

export default function Portal() {
  const [secret, setSecret] = useState(() => localStorage.getItem(LS_SECRET) || "");
  const isolir = new URLSearchParams(window.location.search).get("isolir") === "1";
  const [info, setInfo] = useState(null);
  const [portalBank, setPortalBank] = useState(null);
  useEffect(() => { api.get("/bank-accounts/public", { params: { scope: "portal" } }).then((r) => setPortalBank(r.data[0] || null)).catch(() => {}); }, []);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [paying, setPaying] = useState(false);
  const [readAt, setReadAt] = useState(() => localStorage.getItem(LS_READ) || "");

  const lookup = async (s) => {
    setLoading(true);
    setInfo(null);
    try {
      const { data } = await api.post("/portal/lookup", { secret: s, token: localStorage.getItem(PORTAL_TOKEN_KEY) || "" });
      if (data.token) localStorage.setItem(PORTAL_TOKEN_KEY, data.token);
      setInfo(data);
      localStorage.setItem(LS_SECRET, s);
    } catch (err) {
      toast.error(errMsg(err));
      localStorage.removeItem(LS_SECRET);
      localStorage.removeItem(PORTAL_TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  };
  const cek = (e) => { e.preventDefault(); if (secret.trim()) lookup(secret.trim()); };
  useEffect(() => { const s = localStorage.getItem(LS_SECRET); if (s) lookup(s); }, []); // eslint-disable-line

  const loadChat = useCallback(async () => {
    if (!info) return;
    try {
      const { data } = await api.get(`/portal/chat/${encodeURIComponent(info.name)}/messages`);
      setMessages(data);
    } catch { /* abaikan, coba lagi siklus berikutnya */ }
  }, [info]);

  useEffect(() => {
    if (!info) return undefined;
    loadChat();
    const t = setInterval(loadChat, 5000);
    return () => clearInterval(t);
  }, [info, loadChat]);

  const [replyTarget, setReplyTarget] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);

  const stopRec = (send) => {
    clearInterval(recTimerRef.current);
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") { rec._send = send; rec.stop(); }
    setRecording(false);
  };

  const startRec = async () => {
    if (!info) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Browser tidak mendukung rekam suara");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined);
      recChunksRef.current = [];
      rec._send = true;
      rec.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!rec._send) return;
        const blob = new Blob(recChunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) return;
        setSending(true);
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || "").includes("ogg") ? "ogg" : (rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
          fd.append("file", new File([blob], `suara.${ext}`, { type: rec.mimeType || "audio/webm" }));
          await api.post(`/portal/chat/${encodeURIComponent(info.name)}/voice`, fd);
          toast.success("Pesan suara terkirim — otomatis terhapus dalam 2 jam");
          loadChat();
        } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
      };
      recRef.current = rec;
      rec.start(250);
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => {
        setRecSecs((s) => {
          if (s + 1 >= 60) { stopRec(true); return 60; }
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Izin mikrofon ditolak — aktifkan izin mic di browser");
    }
  };

  useEffect(() => () => { clearInterval(recTimerRef.current); if (recRef.current && recRef.current.state !== "inactive") { recRef.current._send = false; recRef.current.stop(); } }, []);

  const kirim = async (e) => {
    e.preventDefault();
    if (!text.trim() || !info) return;
    setSending(true);
    try {
      await api.post(`/portal/chat/${encodeURIComponent(info.name)}/messages`, { secret: info.name, text: text.trim(), reply_to: replyTarget?.id || "" });
      setText("");
      setReplyTarget(null);
      loadChat();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const [order, setOrder] = useState(null);
  const [panel, setPanel] = useState(null);
  const unread = info ? messages.filter((m) => m.user_id !== `consumer:${info.name}` && m.created_at > readAt).length : 0;
  const lastAdminRef = useRef(null);
  useEffect(() => {
    if (!info) { lastAdminRef.current = null; return; }
    const admin = messages.filter((m) => m.user_id !== `consumer:${info.name}`);
    const last = admin.length ? admin[admin.length - 1].created_at : "";
    if (lastAdminRef.current === null) { lastAdminRef.current = last; return; }
    if (last > lastAdminRef.current) { lastAdminRef.current = last; playPortalReply(); toast("Balasan baru dari admin", { description: admin[admin.length - 1].text || "Pesan suara" }); }
  }, [messages, info]);
  const sisa = info ? daysLeft(info.expired_date) : null;
  useEffect(() => {
    if (panel !== "chat" || !messages.length) return;
    const last = messages[messages.length - 1].created_at;
    if (last > readAt) { setReadAt(last); localStorage.setItem(LS_READ, last); }
  }, [panel, messages, readAt]);
  const keluar = () => { localStorage.removeItem(LS_SECRET); localStorage.removeItem(PORTAL_TOKEN_KEY); setInfo(null); setOrder(null); setMessages([]); setPanel(null); setSecret(""); };
  const bayar = async () => {
    if (!info) return;
    setPaying(true);
    try {
      const { data } = await api.post("/portal/pay", { secret: info.name });
      setOrder(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPaying(false);
    }
  };

  const st = info ? STATUS_MAP[info.status] : null;
  const amount = info ? (info.price || parsePriceFromProfile(info.profile)) : 0;

  return (
    <div className="min-h-screen text-[#F5F7FA]" style={{ background: "#040810", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }} data-testid="portal-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .fn-grad { background: linear-gradient(92deg, #F97316 0%, #EC4899 45%, #2DD4BF 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .fn-shimmer { background: linear-gradient(90deg, #F97316, #EC4899, #2DD4BF, #F97316); background-size: 300% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: fn-shimmer 4s linear infinite; }
        @keyframes fn-shimmer { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
        .fn-card { transition: border-color .2s, transform .2s; }
        .fn-card:hover { border-color: rgba(255,255,255,0.16); }
        .fn-btn { background: #F04E23; transition: transform .15s, filter .15s; }
        .fn-btn:hover { filter: brightness(1.1); }
        .fn-btn:active { transform: scale(.97); }
        .fn-input { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); color: #F5F7FA; border-radius: 0.9rem; padding: 0.65rem 1rem; width: 100%; font-size: 0.9rem; outline: none; transition: border-color .2s; }
        .fn-input:focus { border-color: #F04E23; }
        .fn-input::placeholder { color: #64748B; }
      `}</style>
      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        <div className="text-center space-y-2">
          <div className="inline-block rounded-xl px-5 py-2 border border-white/10" style={{ background: "rgba(255,255,255,0.04)" }}>
            <img src="/logo.png?v=2" alt="Fasnet Fiber" className="h-8 w-auto object-contain" data-testid="portal-logo" />
          </div>
          {!info && (
            <div>
              {isolir && (
                <div className="mb-3 rounded-xl border border-rose-500/40 bg-rose-500/10 p-3" data-testid="portal-isolir-banner">
                  <div className="text-sm font-bold text-rose-300">Masa aktif internet Anda sudah habis</div>
                  <p className="text-xs text-rose-200/80 mt-1">Internet dibatasi sampai pembayaran diterima. Masukkan secret PPPoE Anda di bawah untuk melihat tagihan dan <b>bayar di sini</b> — koneksi pulih otomatis setelah lunas.</p>
                </div>
              )}
              <div className="text-[10px] font-bold tracking-[0.25em] text-teal-300/80 uppercase">Portal Konsumen</div>
              <h1 className="text-2xl font-extrabold tracking-tight mt-1">Cek <span className="fn-grad">Tagihan</span></h1>
              <p className="text-xs text-slate-400 mt-1.5">Masukkan secret PPPoE Anda untuk melihat tagihan, status internet, dan chat admin.</p>
            </div>
          )}
        </div>

        {!info && !loading && (
          <div className={`${CARD} fn-card p-4`}>
            <form onSubmit={cek} className="flex gap-2">
              <input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="contoh: 085820033556_Yamin" className="fn-input font-mono" data-testid="portal-secret-input" />
              <button type="submit" disabled={loading} className="fn-btn text-white text-sm font-bold px-4 rounded-xl inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60" data-testid="portal-check-btn">
                <Search className="w-4 h-4" /> {loading ? "..." : "Cek"}
              </button>
            </form>
          </div>
        )}

        {!info && loading && <div className="text-center text-xs text-slate-400 py-6" data-testid="portal-loading">Memuat data Anda…</div>}

        {info && (
          <>
            <div className={`${CARD} fn-card overflow-hidden`} data-testid="portal-result">
              <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
                <Wifi className="w-4 h-4 text-teal-300" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm font-bold truncate" data-testid="portal-name">{info.name}</div>
                  <div className="text-[11px] font-semibold truncate fn-shimmer" data-testid="portal-cabang">{info.cabang} • <span className="font-mono">{shortProfile(info.profile)}</span></div>
                </div>
                <span className={`text-xs font-bold shrink-0 ${st.cls}`} data-testid="portal-status">{st.label}</span>
                <button type="button" onClick={keluar} title="Keluar" className="text-slate-500 hover:text-white shrink-0" data-testid="portal-logout-btn"><LogOut className="w-4 h-4" /></button>
              </div>
              <div className="p-4 space-y-2.5 text-sm">
                <div className="flex justify-between"><span className={MUTED}>Tarif/bulan</span><span className="font-mono font-bold text-white" data-testid="portal-price">{amount ? rupiah(amount) : "-"}</span></div>
                <div className="flex justify-between"><span className={MUTED}>Berlaku sampai</span><span className="font-mono" data-testid="portal-expired">{fmtTgl(info.expired_date)}</span></div>
                {sisa !== null && sisa <= 3 && amount > 0 && !order && (
                  <div className={`rounded-xl border px-3 py-2 flex items-center gap-2 text-xs ${sisa < 0 ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`} data-testid="portal-due-warning">
                    <Clock className="w-4 h-4 shrink-0" />
                    <span className="font-semibold">{sisa < 0 ? `Masa aktif habis ${-sisa} hari lalu — internet terputus.` : sisa === 0 ? "Masa aktif habis HARI INI." : `Masa aktif habis ${sisa} hari lagi.`} Bayar sekarang agar tetap tersambung.</span>
                  </div>
                )}
                {amount > 0 && !order && (
                  <button onClick={bayar} disabled={paying} className={`fn-btn w-full mt-1 text-white text-sm font-bold py-3 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60 ${sisa !== null && sisa <= 3 ? "animate-pulse" : ""}`} data-testid="portal-pay-btn">
                    <CreditCard className="w-4 h-4" /> {paying ? "Memproses..." : "Lanjutkan Bayar"}
                  </button>
                )}
                {amount > 0 && !order && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-1.5 text-[11px] text-slate-400" data-testid="portal-cara-bayar">
                    <div className="text-xs font-bold text-slate-200">Cara membayar</div>
                    <div><span className="text-[#F04E23] font-bold">1.</span> Klik <b className="text-slate-200">Lanjutkan Bayar</b> — tagihan dibuat dengan nominal unik: <b className="text-slate-200">3 angka terakhir</b> adalah kode unik Anda (nominal sedikit di bawah tarif).</div>
                    <div data-testid="portal-cara-bayar-2"><span className="text-[#F04E23] font-bold">2.</span> Transfer / Scan QRIS <b className="text-slate-200">TEPAT</b> sesuai nominal unik tersebut ke <span className="font-mono text-slate-200">{portalBank ? `${portalBank.bank} ${portalBank.number_fmt}` : "rekening yang tertera pada tagihan"}</span>{portalBank ? ` a.n. ${portalBank.holder}` : ""}. Jika menggunakan QRIS, pastikan Anda memasukkan/mengetik nominal sesuai angka unik tersebut secara manual.</div>
                    <div><span className="text-[#F04E23] font-bold">3.</span> Unggah foto bukti transfer. Setelah admin cocokkan, internet aktif otomatis +30 hari.</div>
                  </div>
                )}
                {order && <PortalTransferCard order={order} onClose={() => setOrder(null)} onChange={setOrder} onOpenChat={() => setPanel("chat")} onPaid={() => api.post("/portal/lookup", { secret: info.name, token: localStorage.getItem(PORTAL_TOKEN_KEY) || "" }).then((r) => setInfo(r.data)).catch(() => {})}
                  invoiceActions={(
                    <div className="flex gap-2 text-[11px]" data-testid="portal-invoice-actions">
                      <button onClick={async () => downloadInvoice(await buildPortalInvoice(info, amount, order))} className="flex-1 py-2 rounded-xl border border-white/10 text-slate-200 inline-flex items-center justify-center gap-1.5 hover:bg-white/5" data-testid="portal-invoice-download">
                        <Download className="w-3.5 h-3.5" /> Unduh Invoice
                      </button>
                      <button onClick={async () => shareInvoiceWA(await buildPortalInvoice(info, amount, order))} className="flex-1 py-2 rounded-xl border border-[#25D366]/40 text-[#25D366] inline-flex items-center justify-center gap-1.5 hover:bg-white/5" data-testid="portal-invoice-share">
                        <Share2 className="w-3.5 h-3.5" /> Share WA
                      </button>
                    </div>
                  )} />}
              </div>
            </div>

            {order?.has_proof && (
            <div className="grid grid-cols-2 gap-2">
              {[["riwayat", Receipt, "Riwayat Bayar"], ["chat", MessageCircle, "Chat Admin"]].map(([k, Icon, label]) => (
                <button key={k} type="button" onClick={() => setPanel(panel === k ? null : k)} className={`${CARD} px-3 py-2.5 text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${panel === k ? "border-teal-300/40 text-teal-300" : "text-slate-300 hover:bg-white/5"}`} data-testid={`portal-panel-${k}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                  {k === "chat" && unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F04E23] text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="portal-chat-unread">{unread}</span>}
                  <ChevronDown className={`w-3 h-3 transition-transform ${panel === k ? "rotate-180" : ""}`} />
                </button>
              ))}
            </div>
            )}

            {panel === "riwayat" && order?.has_proof && <PortalHistory secret={info.name} refreshKey={info.expired_date} />}

            {panel === "chat" && order?.has_proof && (
            <div className={`${CARD} fn-card`} data-testid="portal-chat">
              <div className="p-3 space-y-3">
                <div className="h-52 overflow-y-auto rounded-xl p-3 space-y-2" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)" }} data-testid="portal-chat-list">
                  {messages.map((m) => {
                    const mine = m.user_id === `consumer:${info.name}`;
                    return (
                      <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`portal-msg-${m.id}`}>
                        <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "rounded-br-sm text-white" : "rounded-bl-sm"}`} style={mine ? { background: "#F04E23" } : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
                          {!mine && <div className="text-[10px] font-bold text-teal-300 mb-0.5">{m.name}</div>}
                          {m.reply && (
                            <div className="mb-1 rounded-lg border-l-2 px-2 py-1 text-[11px]" style={mine ? { borderColor: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.12)" } : { borderColor: "#2dd4bf", background: "rgba(45,212,191,0.08)" }} data-testid={`portal-quote-${m.id}`}>
                              <div className="font-bold text-[10px]" style={{ color: mine ? "rgba(255,255,255,0.85)" : "#2dd4bf" }}>{m.reply.name}</div>
                              <div className={`truncate ${mine ? "text-white/70" : "text-slate-400"}`}>{m.reply.text}</div>
                            </div>
                          )}
                          {m.voice_path ? (
                            <div className="min-w-44" data-testid={`portal-voice-${m.id}`}>
                              <audio controls preload="metadata" src={`${API_BASE}/portal/chat/${encodeURIComponent(info.name)}/files/${m.voice_path}?t=${encodeURIComponent(localStorage.getItem(PORTAL_TOKEN_KEY) || "")}`} className="h-9 w-full max-w-60" />
                              <div className={`text-[9px] mt-0.5 ${mine ? "text-white/70" : "text-slate-500"}`}>Pesan suara • terhapus otomatis 2 jam setelah terkirim</div>
                            </div>
                          ) : (
                            <div>{m.text}</div>
                          )}
                          <div className={`text-[9px] mt-0.5 flex items-center gap-2 ${mine ? "text-white/70 justify-end" : "text-slate-500"}`}>
                            {fmtDateTime(m.created_at)}
                            <button type="button" onClick={() => setReplyTarget(m)} className="inline-flex items-center gap-0.5 opacity-60 md:opacity-0 md:group-hover:opacity-100 hover:text-teal-300 transition-opacity" data-testid={`portal-reply-${m.id}`}><Reply className="w-2.5 h-2.5" /> Balas</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {!messages.length && <div className="text-center text-xs text-slate-500 py-10">Belum ada pesan. Tulis keluhan Anda, admin akan membalas di sini.</div>}
                </div>
                {replyTarget && (
                  <div className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ borderLeft: "2px solid #2dd4bf", background: "rgba(45,212,191,0.08)" }} data-testid="portal-reply-preview">
                    <Reply className="w-3.5 h-3.5 text-teal-300 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold text-teal-300">Membalas {replyTarget.name}</div>
                      <div className="text-[11px] text-slate-400 truncate">{replyTarget.text || "[Gambar]"}</div>
                    </div>
                    <button type="button" onClick={() => setReplyTarget(null)} className="text-slate-500 hover:text-white transition-colors" data-testid="portal-reply-cancel"><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <form onSubmit={kirim} className="flex gap-2">
                  <button type="button" onClick={() => (recording ? stopRec(true) : startRec())} title={recording ? "Berhenti & kirim pesan suara" : "Rekam pesan suara (maks 60 detik, terhapus otomatis 2 jam)"} className={`p-2.5 rounded-xl shrink-0 transition-colors ${recording ? "bg-rose-500 text-white animate-pulse" : "fn-btn-outline text-teal-300"}`} data-testid="portal-chat-mic">
                    {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                  {recording ? (
                    <div className="fn-input flex items-center gap-2 text-rose-400 text-xs font-mono" data-testid="portal-chat-recording">
                      <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> Merekam 0:{String(recSecs).padStart(2, "0")} / 1:00
                    </div>
                  ) : (
                    <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Tulis keluhan..." className="fn-input" data-testid="portal-chat-input" />
                  )}
                  <button type="submit" disabled={sending || !text.trim() || recording} className="fn-btn text-white p-2.5 rounded-xl shrink-0 disabled:opacity-50" data-testid="portal-chat-send"><Send className="w-4 h-4" /></button>
                </form>
              </div>
            </div>
            )}
          </>
        )}

        <div className="text-center text-[10px] leading-relaxed text-slate-500 font-mono pt-2" data-testid="portal-footer">
          <span className="font-bold text-slate-400">PT. Fasnet Sky Fiber</span> • AS152793 • NIB 0105240016275
        </div>
      </div>
    </div>
  );
}
